"use client";

import { useState } from "react";
import { StarRating } from "./star-rating";
import { api } from "@/lib/api";
import { useAuthStore } from "@/store/auth.store";

interface ReviewFormProps {
  slug: string;
  onSuccess: () => void;
}

interface AddReviewResponse {
  id: string;
  pendingApproval: boolean;
  message: string;
}

export function ReviewForm({ slug, onSuccess }: ReviewFormProps) {
  const { customer } = useAuthStore();
  const [rating, setRating] = useState(0);
  const [name, setName] = useState(customer?.fullName ?? "");
  const [email, setEmail] = useState(customer?.email ?? "");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (rating === 0) { setError("Lütfen puan seçin."); return; }
    if (!name.trim()) { setError("İsim zorunludur."); return; }

    setLoading(true);
    setError("");
    setSuccessMessage("");
    try {
      const { data } = await api.post<AddReviewResponse>(`/api/catalog/products/${slug}/reviews`, {
        reviewerName: name.trim(),
        reviewerEmail: email.trim() || null,
        rating,
        title: title.trim() || null,
        body: body.trim() || null,
      });
      setSuccessMessage(data.message || "Yorumunuz alındı.");
      onSuccess();
      setTitle("");
      setBody("");
    } catch {
      setError("Yorum gönderilemedi. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full h-9 px-3 text-sm border border-gray-200 rounded-[var(--sf-radius)] focus:outline-none focus:border-gray-900 transition-colors bg-white";

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border border-gray-100 rounded-[var(--sf-radius)] p-5">
      <h3 className="font-semibold text-gray-950">Değerlendirme Yaz</h3>

      {/* Yıldız */}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-2">Puanınız *</label>
        <StarRating
          rating={rating}
          size={28}
          interactive
          onChange={(r) => { setRating(r); setError(""); }}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Ad Soyad *</label>
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Adınız"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">E-posta</label>
          <input
            className={inputClass}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="e-posta (isteğe bağlı)"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Başlık</label>
        <input
          className={inputClass}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Kısa bir başlık (isteğe bağlı)"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Yorum</label>
        <textarea
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-[var(--sf-radius)] focus:outline-none focus:border-gray-900 transition-colors resize-none bg-white"
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Deneyiminizi paylaşın..."
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}
      {successMessage && <p className="text-sm text-emerald-600">{successMessage}</p>}

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-black text-white text-sm font-semibold py-2.5 rounded-[var(--sf-radius)] hover:bg-gray-800 transition-colors disabled:opacity-50"
      >
        {loading ? "Gönderiliyor..." : "Değerlendirme Gönder"}
      </button>
    </form>
  );
}
